/**
 * Form Submission History Component
 * 
 * Displays submission history for a specific form and patient with ability to:
 * - View all submissions for this form/patient combination
 * - Change submission status (addendum, invalidate, acknowledge)
 * - View action history for each submission
 * - Perform post-submission actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Dropdown, Spinner, Alert, Form as BootstrapForm } from 'react-bootstrap';
import { FaHistory, FaCheckCircle, FaTimesCircle, FaCommentDots, FaEye, FaChevronDown, FaChevronRight, FaPaperPlane, FaDownload, FaCopy, FaUndo } from 'react-icons/fa';
import toast from 'react-hot-toast';
import moment from 'moment';

// Services
import { formSubmissionActionsService } from '../../services/formSubmissionActionsService';
import dynamicFormsService from '../../services/dynamicFormsService';
import { getSubmissionHistoryAction, getPatientDataAction, getSubmissionByIdAction } from '../../Action/admin.action';

const FormSubmissionHistory = ({ formId, patientId, currentSubmissionId, onDraftLoaded, onTemplateLoaded }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);
  const [submissionActions, setSubmissionActions] = useState({});
  const [expandedSubmissions, setExpandedSubmissions] = useState(new Set());
  const [expandedDateGroups, setExpandedDateGroups] = useState(new Set());
  const [formSchema, setFormSchema] = useState(null);
  const [fieldMap, setFieldMap] = useState(null); // 🗺️ Cross-version field map
  const [patientInfo, setPatientInfo] = useState(null);
  const [fullSubmissionData, setFullSubmissionData] = useState({});
  const [loadingSubmissions, setLoadingSubmissions] = useState({});
  const [loadingPatient, setLoadingPatient] = useState(false);

  // Draft operations state
  const [submittingDraft, setSubmittingDraft] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    limit: 20,
    hasMore: false,
    nextCursor: null,
    prevCursor: null,
    currentPage: 1,
    totalItems: 0
  });
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchSubmissions = useCallback(async (cursor = null, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        console.log('🔄 Setting loadingMore state to true');
        setLoadingMore(true);
      } else {
        console.log('🔄 Setting loading state to true');
        setLoading(true);
      }

      console.log(`🔍 Fetching submissions for formId: ${formId}, patientId: ${patientId}`, { cursor, isLoadMore });

      // Use the new pagination endpoint
      const paginationParams = {
        limit: pagination.limit,
        cursor: cursor,
        sortBy: 'submitted_at',
        sortOrder: 'desc'
      };

      const historyResponse = await getSubmissionHistoryAction(formId, patientId, paginationParams);

      console.log('📊 History response:', historyResponse);

      if (historyResponse.success && historyResponse.data) {
        // Handle the new pagination response format
        const responseData = historyResponse.data;
        const newSubmissions = responseData.items || [];
        const hasMore = responseData.has_more || false;
        const nextCursor = responseData.next_cursor || null;
        const totalItems = responseData.total || 0;

        console.log(`📋 New submissions found: ${newSubmissions.length}`);
        console.log('📋 Pagination info:', { hasMore, nextCursor, totalItems });

        // Update submissions based on whether we're loading more or starting fresh
        if (isLoadMore) {
          setSubmissions(prevSubmissions => {
            // Combine existing and new submissions, avoiding duplicates
            const existingIds = new Set(prevSubmissions.map(s => s.submission_id));
            const uniqueNewSubmissions = newSubmissions.filter(s => !existingIds.has(s.submission_id));
            return [...prevSubmissions, ...uniqueNewSubmissions];
          });
        } else {
          setSubmissions(newSubmissions);
        }

        // Update pagination state
        setPagination(prev => ({
          ...prev,
          hasMore,
          nextCursor,
          totalItems,
          currentPage: isLoadMore ? prev.currentPage + 1 : 1
        }));

        // Reset loading states before returning
        if (isLoadMore) {
          console.log('🔄 Resetting loadingMore state to false (success path)');
          setLoadingMore(false);
        } else {
          console.log('🔄 Resetting loading state to false (success path)');
          setLoading(false);
        }

        return;
      }
      // Fallback to old method
      const loginData = JSON.parse(localStorage.getItem('loginSuccessNeurulaHealth') || '{}');
      const authToken = loginData.authToken;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/dynamic-forms/forms/${formId}/submissions/patient/${patientId}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.submissions) {
          setSubmissions(result.submissions);
        }
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Error loading submission history');
      setSubmissions([]);

      // Reset loading states on error
      if (isLoadMore) {
        console.log('🔄 Resetting loadingMore state to false (error path)');
        setLoadingMore(false);
      } else {
        console.log('🔄 Resetting loading state to false (error path)');
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, patientId]); // Dependencies intentionally limited to prevent infinite loops

  const fetchFormSchema = useCallback(async () => {
    try {
      const response = await dynamicFormsService.getFormById(formId);
      if (response.success && response.data) {
        setFormSchema(response.data);
      }
    } catch (error) {
      console.error('Error fetching form schema:', error);
    }
  }, [formId]);

  // 🗺️ Fetch field map for cross-version field resolution
  const fetchFieldMap = useCallback(async () => {
    try {
      console.log(`🗺️  Fetching field map for form ${formId}...`);
      const fieldMapData = await dynamicFormsService.getFormFieldMap(formId);
      if (fieldMapData && fieldMapData.field_map) {
        setFieldMap(fieldMapData);
        console.log(`✅ Field map loaded: ${Object.keys(fieldMapData.field_map).length} unique fields across ${fieldMapData.version_count} versions`);
      }
    } catch (error) {
      console.error('Error fetching field map:', error);
      // Not critical - fallback to form schema only
    }
  }, [formId]);

  const fetchPatientInfo = useCallback(async () => {
    if (!patientId || patientInfo || loadingPatient) return;

    try {
      setLoadingPatient(true);

      // Check if patient data is already available in localStorage or global state
      const cachedPatientData = localStorage.getItem(`patient_${patientId}`);
      if (cachedPatientData) {
        try {
          const parsed = JSON.parse(cachedPatientData);
          const cacheAge = Date.now() - parsed.timestamp;
          // Cache for 5 minutes
          if (cacheAge < 5 * 60 * 1000) {
            setPatientInfo(parsed.data);
            return;
          }
        } catch (e) {
          // Invalid cache, continue with API call
        }
      }

      // Try direct API call first
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/patients/${patientId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('loginSuccessNeurulaHealth'))?.authToken || ''}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Cache the patient data
          localStorage.setItem(`patient_${patientId}`, JSON.stringify({
            data: result.data,
            timestamp: Date.now()
          }));
          setPatientInfo(result.data);
          return;
        }
      }

      // Fallback to getPatientDataAction
      const fallbackResponse = await getPatientDataAction({ patientId });
      if (fallbackResponse.success && fallbackResponse.data) {
        // Extract the actual patient data from the response
        const patientData = fallbackResponse.data.data || fallbackResponse.data;
        // Cache the patient data
        localStorage.setItem(`patient_${patientId}`, JSON.stringify({
          data: patientData,
          timestamp: Date.now()
        }));
        setPatientInfo(patientData);
      }
    } catch (error) {
      console.error('Error fetching patient info:', error);
    } finally {
      setLoadingPatient(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]); // Dependencies intentionally limited to prevent infinite loops

  const fetchFullSubmissionData = useCallback(async (submissionId) => {
    if (!submissionId || fullSubmissionData[submissionId]) return;

    try {
      setLoadingSubmissions(prev => ({ ...prev, [submissionId]: true }));

      // Check cache first
      const cachedSubmissionData = localStorage.getItem(`submission_${submissionId}`);
      if (cachedSubmissionData) {
        try {
          const parsed = JSON.parse(cachedSubmissionData);
          const cacheAge = Date.now() - parsed.timestamp;
          // Cache for 2 minutes
          if (cacheAge < 2 * 60 * 1000) {
            setFullSubmissionData(prev => ({
              ...prev,
              [submissionId]: parsed.data
            }));
            return;
          }
        } catch (e) {
          // Invalid cache, continue with API call
        }
      }

      // Find the submission to determine if it's a draft or submission
      const submission = submissions.find(s => s.submission_id === submissionId);
      let response;

      if (submission && submission.submission_status === 'draft') {
        // For drafts, use the draft endpoint
        response = await dynamicFormsService.getDraftById(formId, submissionId);
      } else {
        // For submissions, use the submission endpoint
        response = await getSubmissionByIdAction(submissionId);
      }

      if (response.success && response.data) {
        // Cache the submission data
        localStorage.setItem(`submission_${submissionId}`, JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }));
        setFullSubmissionData(prev => ({
          ...prev,
          [submissionId]: response.data
        }));
      }
    } catch (error) {
      console.error('Error fetching full submission data:', error);
    } finally {
      setLoadingSubmissions(prev => ({ ...prev, [submissionId]: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissions, formId]); // Added dependencies for submissions and formId

  useEffect(() => {
    if (formId && patientId) {
      fetchSubmissions();
      fetchFormSchema();
      fetchFieldMap(); // 🗺️ Load field map for cross-version support
      fetchPatientInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, patientId]); // Functions intentionally omitted from dependencies to prevent infinite loops

  // Initialize first date group as expanded
  useEffect(() => {
    const groupedSubmissions = groupSubmissionsByDate(submissions);
    if (groupedSubmissions.length > 0 && expandedDateGroups.size === 0) {
      setExpandedDateGroups(new Set([groupedSubmissions[0].dateKey]));
    }
  }, [submissions, expandedDateGroups.size]);

  // Cleanup old cache entries on component mount
  useEffect(() => {
    const cleanupCache = () => {
      const now = Date.now();
      const keysToRemove = [];

      // Clean up old patient cache (older than 10 minutes)
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('patient_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (now - data.timestamp > 10 * 60 * 1000) {
              keysToRemove.push(key);
            }
          } catch (e) {
            keysToRemove.push(key);
          }
        }
      }

      // Clean up old submission cache (older than 5 minutes)
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('submission_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (now - data.timestamp > 5 * 60 * 1000) {
              keysToRemove.push(key);
            }
          } catch (e) {
            keysToRemove.push(key);
          }
        }
      }

      // Remove old entries
      keysToRemove.forEach(key => localStorage.removeItem(key));
    };

    cleanupCache();
  }, []);

  const fetchSubmissionActions = async (submissionId) => {
    try {
      const response = await formSubmissionActionsService.getSubmissionActions(submissionId);
      if (response.success) {
        setSubmissionActions(prev => ({
          ...prev,
          [submissionId]: response.data || []
        }));
      } else {
        // Set empty array for failed requests to prevent re-fetching
        setSubmissionActions(prev => ({
          ...prev,
          [submissionId]: []
        }));
      }
    } catch (error) {
      console.error('Error fetching submission actions:', error);
      // Set empty array for failed requests to prevent re-fetching
      setSubmissionActions(prev => ({
        ...prev,
        [submissionId]: []
      }));
    }
  };

  const handleActionClick = (submission, type) => {
    // Prevent actions on invalidated submissions
    if (submission.submission_status === 'invalidated') {
      toast.error('Cannot perform actions on invalidated submissions');
      return;
    }

    setSelectedSubmission(submission);
    setActionType(type);
    setActionReason('');
    setShowActionModal(true);
  };

  const handleUseAsTemplate = async (submission) => {
    try {
      // Use the service method instead of direct fetch
      const result = await dynamicFormsService.useSubmissionAsTemplate(submission.submission_id);

      if (result.success) {

        // Store template data in localStorage for the form to load
        // The backend returns template_data directly
        const templateData = result.template_data;

        localStorage.setItem(`template_${formId}_${patientId}`, JSON.stringify({
          data: templateData,
          timestamp: Date.now(),
          source_submission_id: submission.submission_id
        }));

        toast.success('Template data loaded successfully. You can now use this data to fill the form.');

        // Notify parent component to reload template data
        if (typeof onTemplateLoaded === 'function') {
          onTemplateLoaded(templateData);
        }
      } else {
        toast.error(result.message || 'Failed to load template data');
      }
    } catch (error) {
      console.error('Error using submission as template:', error);
      toast.error('Error loading template data');
    }
  };

  const handleSubmitAction = async () => {
    if (!actionReason.trim()) {
      toast.error('Please provide a reason for this action');
      return;
    }

    try {
      setSubmittingAction(true);
      const actionData = {
        submission_id: selectedSubmission.submission_id,
        action_type: actionType,
        reason: actionReason.trim(),
      };

      const response = await formSubmissionActionsService.createSubmissionAction(
        selectedSubmission.submission_id,
        actionData
      );

      if (response.success) {
        toast.success(`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} submitted successfully`);
        setShowActionModal(false);

        // Immediately update the local state for the specific submission
        setSubmissions(prevSubmissions =>
          prevSubmissions.map(submission => {
            if (submission.submission_id === selectedSubmission.submission_id) {
              // Update the status based on the action type
              let newStatus = submission.submission_status;
              if (actionType === 'invalidate') {
                newStatus = 'invalidated';
              } else if (actionType === 'acknowledge') {
                newStatus = 'acknowledged';
              }

              return {
                ...submission,
                submission_status: newStatus
              };
            }
            return submission;
          })
        );

        // Refresh submissions to ensure we have the latest data
        await fetchSubmissions();

        // Refresh actions for this submission
        await fetchSubmissionActions(selectedSubmission.submission_id);
      } else {
        toast.error(response.error || 'Failed to submit action');
      }
    } catch (error) {
      console.error('Error submitting action:', error);
      toast.error('Error submitting action');
    } finally {
      setSubmittingAction(false);
    }
  };

  // Draft operations
  const handleSubmitDraft = async (submission) => {
    try {
      setSubmittingDraft(true);

      const response = await dynamicFormsService.submitDraft(formId, submission.submission_id);

      if (response.success) {
        toast.success('Draft submitted successfully');
        // Refresh submissions to show updated status
        await fetchSubmissions();
      } else {
        toast.error(response.message || 'Failed to submit draft');
      }
    } catch (error) {
      console.error('Error submitting draft:', error);
      toast.error('Error submitting draft');
    } finally {
      setSubmittingDraft(false);
    }
  };

  const handleLoadDraft = async (submission) => {
    try {
      setLoadingDraft(true);

      // Get the draft data
      const response = await dynamicFormsService.getDraftById(formId, submission.submission_id);

      if (response.success) {
        console.log('📝 Draft response structure:', response);

        // Store draft data in localStorage for the form to load
        localStorage.setItem(`draft_${formId}_${patientId}`, JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }));

        toast.success('Draft loaded successfully. You can now continue editing.');

        // Convert draft data array to object format for the form
        const draftDataObject = {};
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          response.data.data.forEach(fieldData => {
            draftDataObject[fieldData.field_id] = fieldData.value;
          });
        }

        console.log('📝 Converted draft data:', draftDataObject);

        // Notify parent component to reload draft data
        if (typeof onDraftLoaded === 'function') {
          onDraftLoaded(draftDataObject, {
            submission_id: submission.submission_id,
            draft_id: response.data.draft_id || submission.submission_id,
            form_id: formId,
            draft_saved_at: response.data.draft_saved_at || submission.draft_saved_at || submission.submitted_at,
            status: 'draft'
          });
        }
      } else {
        toast.error(response.message || 'Failed to load draft');
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      toast.error('Error loading draft');
    } finally {
      setLoadingDraft(false);
    }
  };

  // Pagination functions
  const loadMoreSubmissions = useCallback(async () => {
    if (pagination.hasMore && pagination.nextCursor && !loadingMore) {
      await fetchSubmissions(pagination.nextCursor, true);
    }
  }, [pagination.hasMore, pagination.nextCursor, loadingMore, fetchSubmissions]);

  const refreshSubmissions = useCallback(async () => {
    // Reset pagination and fetch fresh data
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      nextCursor: null,
      hasMore: false
    }));
    await fetchSubmissions();
  }, [fetchSubmissions]);

  const toggleSubmissionDetails = (submissionId) => {
    const newExpanded = new Set(expandedSubmissions);
    if (newExpanded.has(submissionId)) {
      newExpanded.delete(submissionId);
    } else {
      newExpanded.add(submissionId);
      // Fetch actions and full submission data when expanding
      if (!submissionActions[submissionId]) {
        fetchSubmissionActions(submissionId);
      }
      fetchFullSubmissionData(submissionId);
    }
    setExpandedSubmissions(newExpanded);
  };

  const toggleDateGroup = (dateKey) => {
    const newExpanded = new Set(expandedDateGroups);
    if (newExpanded.has(dateKey)) {
      newExpanded.delete(dateKey);
    } else {
      newExpanded.add(dateKey);
    }
    setExpandedDateGroups(newExpanded);
  };

  const getStatusBadge = (submission) => {
    const status = submission.submission_status || 'submitted';
    const variants = {
      draft: 'secondary',
      submitted: 'success',
      updated: 'info',
      invalidated: 'danger',
      acknowledged: 'primary'
    };

    return (
      <Badge bg={variants[status] || 'secondary'} className={status === 'invalidated' ? 'fw-bold' : ''}>
        {status === 'invalidated' ? '❌ ' : ''}{status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getActionButtons = (submission) => {
    // Don't show action buttons for the current submission being filled
    if (submission.submission_id === currentSubmissionId) {
      return null;
    }

    const isDraft = submission.submission_status === 'draft';
    const isInvalidated = submission.submission_status === 'invalidated';

    // If submission is invalidated, show disabled actions button
    if (isInvalidated) {
      return (
        <Dropdown>
          <Dropdown.Toggle variant="outline-secondary" size="sm" disabled>
            Actions
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item disabled className="text-muted">
              <FaTimesCircle className="me-2" />
              No actions available for invalidated submissions
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      );
    }

    return (
      <Dropdown>
        <Dropdown.Toggle variant="outline-primary" size="sm">
          Actions
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {/* Draft-specific actions - only for drafts, not invalidated */}
          {isDraft && (
            <>
              <Dropdown.Item
                onClick={() => handleSubmitDraft(submission)}
                disabled={submittingDraft}
              >
                {submittingDraft ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <FaPaperPlane className="me-2" />
                )}
                Submit Draft
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleLoadDraft(submission)}
                disabled={loadingDraft}
              >
                {loadingDraft ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <FaDownload className="me-2" />
                )}
                Load Draft
              </Dropdown.Item>
              <Dropdown.Divider />
            </>
          )}

          {/* Regular actions - only for non-invalidated submissions */}
          <Dropdown.Item onClick={() => handleActionClick(submission, 'addendum')}>
            <FaCommentDots className="me-2" />
            Add Addendum
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleActionClick(submission, 'acknowledge')}>
            <FaCheckCircle className="me-2" />
            Acknowledge
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleActionClick(submission, 'invalidate')}>
            <FaTimesCircle className="me-2" />
            Invalidate
          </Dropdown.Item>
          {/* Only show "Use as Template" for non-draft, non-invalidated submissions */}
          {!isDraft && (
            <Dropdown.Item onClick={() => handleUseAsTemplate(submission)}>
              <FaCopy className="me-2" />
              Use as Template
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  // Comprehensive HD Forms Field Mapping (all three forms)
  const HD_FIELD_MAPPING = {
    // Pre-HD Form Fields (dfa4c262-1509-4aaf-8a86-8eb9bd0daf38)
    '16727c01-815f-4073-87e8-0a4cda7bec3b': 'Session Date',
    '79f3ccc2-19fa-4393-be46-032abcb1316a': 'Session Time',
    'a2c4f15e-402a-4001-9ace-09217a39c9f2': 'Session ID',
    '328aa672-cfbc-41fa-a3b8-165402e20f3a': 'Priming Volume (ml)',
    '2735a29e-74fb-4b30-b15d-c01e26049623': 'UF Recirculation Volume (ml)',
    'a70460f3-be2f-4d34-9dbe-57e0bbd580d2': 'Water Hardness',
    '2d465c38-ed78-4dfb-b87d-073e29b7db35': 'Chlorine Level',
    '2550c443-b901-4219-bae3-667db9e6a180': 'Dry Weight/Target Weight (kg)',
    'a297ebf8-37bd-4030-aaa6-cb408a4f03e4': 'UF Goal (liters)',
    '57a844d1-416a-4b7a-8abf-2dbeb5ece9a1': 'Planned Duration (hours)',
    '058b7ecb-2851-45b3-b593-b9a8999b00ef': 'Blood Flow Rate (mL/min)',
    'dc41fbc9-1a21-4a5c-847c-d42f4a0cf08e': 'Dialysate Flow Rate (mL per minute)',
    '2f2c21e2-1c00-46d3-bc3c-469b771f2f3f': 'Dialyzer',
    'c0b1a24d-d187-4d53-8c5e-ddbb43df96ee': 'Ultrafiltration',
    '51598ad7-b9f6-4e7e-ab6e-290921d5c4d7': 'Iso-UF',
    'a0cc728d-7b68-42d8-96fb-d76e490b9488': 'Na Profiler',
    '77c5a8da-a675-45c2-8927-371c94ee6502': 'Starting Na',
    '1e4a3297-6529-4adf-9fbc-be9d15b3394d': 'Ending Na',
    '3c346ec9-f7db-4be2-b48c-e8c5060b16e3': 'UF Profiler',
    'd508eb77-8938-498a-a101-b9a23c1d1da9': 'Concentrate',

    // During-HD Form Fields (92f6c89b-1531-4560-a648-7b778524900a)
    '88547fda-e497-4101-9757-ec4bf4a33ce6': 'Monitoring Time',
    '6c46fea6-6bf1-4694-8f35-82e11770864a': 'Blood Pressure (mmHg)',
    '19dda464-ec78-4621-8743-a0c02fd0ffa9': 'Pulse Rate (bpm)',
    'd717e34d-1fc6-439b-aecb-8f783e0b3690': 'Temperature (°C)',
    '77dfa3cb-d06b-45df-8758-641df79dd408': 'Respiratory Rate (per min)',
    '3540f032-6c6c-431e-a826-e37e13226983': 'Blood Sugar (mg/Dl)',
    '71fe3f36-0d73-4b79-bd62-f69e7d7bc54b': 'Arterial Pressure (AP) (mmHg)',
    'a3c38c3a-39c1-463a-a93d-565fb9a31618': 'Venous Pressure (VP) (mmHg)',
    '5373add1-18cf-4fd6-ab11-d6dfc554b524': 'Conductivity (mS/cm)',
    '2571b669-d047-4611-890f-dcd475135ee3': 'Anticoagulation (IU)',
    '7fcf103d-2c72-40bc-a6bb-07c851784431': 'Patient Status',
    'e59a3d9a-c281-401e-a037-1e1a0df1e362': 'Pain Score (0-10)',
    'd4026781-f5f3-4448-95cc-bab55eaa913d': 'Complications',
    '6753f8ff-ce71-4524-ac22-4bd0be0ae92a': 'Treatment Remarks',
    '44e3ed0f-2c9d-4671-8897-a68400e15182': 'Detailed Clinical Notes',

    // Post-HD Form Fields (c1b477de-0524-410d-a0d4-8b0b00650d1f)
    '66047239-04d5-453a-bf39-38b969b8fb27': 'Treatment Completion Time',
    '012334cb-57b6-484a-b119-fb4ad2029ce8': 'Actual Treatment Duration (hours)',
    '53aade7d-7019-4a8e-8bb6-e4d3e457d6eb': 'Post-HD Blood Pressure (mmHg)',
    '1146d96d-3e9b-4cd0-a04d-faa44f27d33a': 'Post-HD Pulse Rate (bpm)',
    '0054e275-94d1-41c2-b5b6-e19d537d7463': 'Post-HD Temperature (°C)',
    '1bec29b2-e45a-4bef-941e-82ddaa785636': 'Post-HD Respiratory Rate (per min)',
    '1f827eff-23b3-4520-b126-dd3345d3842f': 'Post-HD Blood Sugar (mg/Dl)',
    '46761298-3eda-40af-b6c0-2cbbc4659fdb': 'Post-HD Weight (kg)',
    '54b8c437-96ca-465b-975b-e0f2eb5015f5': 'Weight Loss (kg)',
    'ebc03c3d-6ae8-4c50-9314-941d6e7bc10e': 'Total UF Removed (liters)',
    'e4fbb6a0-1d63-4d0b-856f-7c973e2577cd': 'Kt/V Ratio',
    '5a7f8a4a-529a-41bf-9ddf-2c1aaabdba02': 'Accumulated Blood Volume (liters)',
    '5188ceee-087e-4aee-b02e-77bbae584832': 'Hemostasis Time (minutes)',
    '7791625c-8ed8-4748-8f1c-864a41ec29d6': 'Catheter Lock Type',
    'dbf2ac3b-d771-4d2b-b685-aa980c8621da': 'Arterial Lock Volume (mL)',
    'bc513a6e-170c-4e08-b6f3-7853b7a65ce9': 'Venous Lock Volume (mL)',
    '1248b2a5-e097-472e-a4f4-b629d50d9362': 'UF Goal Met',
    '4b6780fc-d49f-4ecd-b579-403f661221b9': 'Post-HD Neurological Status',
    'fdfb211e-fd29-4257-97ed-49fc5c9ac45a': 'Post-HD Cardiovascular Status',
    'f4c28d1d-ce2a-4982-9144-2494479a8f3e': 'Post-HD Respiratory Status'
  };

  /**
   * Extract base field ID from composite keys
   * Examples:
   *   "uuid_0" → "uuid"
   *   "uuid_1" → "uuid"
   *   "uuid" → "uuid" (no change for non-composite keys)
   */
  const extractBaseFieldId = (fieldId) => {
    if (!fieldId) return fieldId;

    // If the original field_id exists in the field map, use it as-is
    if (fieldMap && fieldMap.field_map && fieldMap.field_map[fieldId]) {
      return fieldId;
    }

    const parts = fieldId.split('_');
    const lastPart = parts[parts.length - 1];

    // Only strip suffix if it looks like a composite key AND base ID exists in field map
    if (parts.length >= 2 && !isNaN(lastPart) && lastPart === parseInt(lastPart).toString()) {
      const baseId = parts.slice(0, -1).join('_');
      if (fieldMap && fieldMap.field_map && fieldMap.field_map[baseId]) {
        return baseId;
      }
    }

    // Return original field_id unchanged
    return fieldId;
  };

  // Helper function to get field name from field ID
  const getFieldName = (fieldId) => {
    // Extract base field ID from composite keys (e.g., "uuid_0" → "uuid")
    const baseFieldId = extractBaseFieldId(fieldId);

    // 🗺️ PRIORITY 1: Try field map for cross-version support
    if (fieldMap && fieldMap.field_map && fieldMap.field_map[baseFieldId]) {
      const fieldInfo = fieldMap.field_map[baseFieldId];
      const label = fieldInfo.current_label || fieldInfo.label;

      // Add badge for removed fields
      if (fieldInfo.is_removed) {
        return (
          <>
            {label}
            <Badge bg="warning" className="ms-2" style={{ fontSize: '0.7em' }}>
              Removed in v{fieldInfo.removed_in_version}
            </Badge>
          </>
        );
      }

      return label;
    }

    // PRIORITY 2: Try current form schema
    if (formSchema && formSchema.fields) {
      const field = formSchema.fields.find(f => f.field_id === baseFieldId);
      if (field) {
        return field.label || field.name || baseFieldId;
      }
    }

    // PRIORITY 3: Fallback to HD field mapping for cross-form field IDs
    if (HD_FIELD_MAPPING[baseFieldId]) {
      return HD_FIELD_MAPPING[baseFieldId];
    }

    // Final fallback to shortened field ID
    return baseFieldId ? `Unknown (${baseFieldId.substring(0, 8)}...)` : 'Unknown Field';
  };

  // Helper function to get user name
  const getUserName = (userId, name) => {
    return name || userId || 'Unknown';
  };

  // Helper function to get patient MRN
  const getPatientMRN = useCallback(() => {
    if (!patientInfo) {
      return patientId ? <><Spinner animation="border" size="sm" className="me-2" />Loading...</> : 'Unknown Patient';
    }
    // Handle case where patientInfo is the full response object
    const actualPatientData = patientInfo.data || patientInfo;
    const mrn = actualPatientData.current_mrn ||
      actualPatientData.mrn_permanent ||
      actualPatientData.mrn_temporary ||
      actualPatientData.mrnNumber ||
      patientId || 'Unknown Patient';
    return mrn;
  }, [patientInfo, patientId]);

  // Helper function to group submissions by date
  const groupSubmissionsByDate = (submissions) => {
    const groups = {};

    submissions.forEach(submission => {
      // Use draft_saved_at for drafts, submitted_at for others
      const dateToUse = submission.submission_status === 'draft'
        ? (submission.draft_saved_at || submission.submitted_at)
        : submission.submitted_at;

      const dateKey = moment(dateToUse).format('YYYY-MM-DD');

      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: moment(dateToUse).format('DD/MM/YYYY'),
          dateKey: dateKey,
          submissions: []
        };
      }

      groups[dateKey].submissions.push(submission);
    });

    // Sort groups by date (newest first) and sort submissions within each group
    return Object.values(groups)
      .sort((a, b) => moment(b.dateKey).diff(moment(a.dateKey)))
      .map(group => ({
        ...group,
        submissions: group.submissions.sort((a, b) => {
          const dateA = a.submission_status === 'draft'
            ? (a.draft_saved_at || a.submitted_at)
            : a.submitted_at;
          const dateB = b.submission_status === 'draft'
            ? (b.draft_saved_at || b.submitted_at)
            : b.submitted_at;
          return moment(dateB).diff(moment(dateA));
        })
      }));
  };

  // Format field value based on type
  const formatFieldValue = (field, value) => {
    // Handle empty values
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted fst-italic">Not provided</span>;
    }

    // Detect base64 image data (signatures)
    if (typeof value === 'string' && value.startsWith('data:image/')) {
      return (
        <div className="signature-preview mt-1">
          <img
            src={value}
            alt="Signature"
            style={{
              maxWidth: '100%',
              maxHeight: '150px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              display: 'block'
            }}
          />
        </div>
      );
    }

    // Handle arrays (checkbox fields)
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    // Handle objects
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    // Handle dates
    if (field.field_type === 'date' || field.field_type === 'datetime') {
      try {
        return new Date(value).toLocaleString();
      } catch {
        return String(value);
      }
    }

    // Handle booleans (checkbox)
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Default: convert to string
    return String(value);
  };

  const renderFormData = (submission) => {
    const submissionId = submission.submission_id;
    const fullData = fullSubmissionData[submissionId];
    const isLoading = loadingSubmissions[submissionId];

    if (isLoading) {
      return (
        <div className="mt-3">
          <h6>Form Data</h6>
          <div className="text-center p-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading form data...
          </div>
        </div>
      );
    }

    // Handle different possible data structures
    let fieldData = [];
    if (fullData) {
      if (Array.isArray(fullData.data)) {
        fieldData = fullData.data;
      } else if (fullData.data && Array.isArray(fullData.data.data)) {
        fieldData = fullData.data.data;
      } else if (Array.isArray(fullData)) {
        fieldData = fullData;
      }
    }

    if (fieldData.length === 0) {
      return (
        <div className="mt-3">
          <h6>Form Data</h6>
          <div className="text-muted">
            <small>No form data available</small>
          </div>
        </div>
      );
    }

    return (
      <div className="row">
        {fieldData.map((field, index) => {
          const fieldName = getFieldName(field.field_id);
          const fieldType = field.field_type || 'text';

          return (
            <div key={field.field_id || index} className="col-md-6 mb-2">
              <div className="form-field-data p-2 border rounded">
                <div className="d-flex justify-content-between align-items-center">
                  <strong className="text-primary">{fieldName}:</strong>
                  <Badge bg="secondary" className="small">{fieldType}</Badge>
                </div>
                <div className="mt-1 field-value-container">
                  {formatFieldValue(field, field.value)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderActionHistory = (submissionId) => {
    const actions = submissionActions[submissionId] || [];

    if (actions.length === 0) {
      return (
        <div className="mt-3">
          <h6>Action History</h6>
          <div className="p-3 text-muted bg-white border rounded">
            <small>No actions recorded for this submission</small>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-3">
        <h6>Action History</h6>
        <div className="action-history bg-white border rounded p-3">
          {actions.map((action, index) => (
            <div key={action.action_id || index} className="action-item border-start border-3 ps-3 mb-2">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong className="text-capitalize">{action.action_type}</strong>
                  <div className="text-muted small">
                    by {getUserName(action.performed_by, action.performed_by_name)} • {moment(action.performed_at).format('DD/MM/YYYY hh:mm A')}
                  </div>
                  <div className="mt-1">{action.reason}</div>
                </div>
                <Badge bg={
                  action.action_type === 'acknowledge' ? 'success' :
                    action.action_type === 'invalidate' ? 'danger' :
                      action.action_type === 'addendum' ? 'info' : 'secondary'
                }>
                  {action.action_type}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="mt-4">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading submission history...
        </Card.Body>
      </Card>
    );
  }

  if (!submissions || submissions.length === 0) {
    return (
      <Card className="mt-4">
        <Card.Header>
          <FaHistory className="me-2" />
          Submission History
        </Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-0">
            No previous submissions found for this form and patient.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  // Group submissions by date
  const groupedSubmissions = groupSubmissionsByDate(submissions);

  return (
    <>
      <Card className="mt-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <FaHistory className="me-2" />
              Submission History ({submissions.length} submissions)
            </div>
            <Button variant="outline-secondary" size="sm" onClick={refreshSubmissions}>
              Refresh
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Pagination Settings */}
          <div className="d-flex justify-content-between align-items-center mb-3 p-2 border rounded bg-light">
            <div className="d-flex align-items-center gap-3">
              <div>
                <BootstrapForm.Label className="form-label small mb-1">Items per page:</BootstrapForm.Label>
                <BootstrapForm.Select
                  size="sm"
                  style={{ width: '80px' }}
                  value={pagination.limit}
                  onChange={(e) => {
                    const newLimit = parseInt(e.target.value);
                    setPagination(prev => ({
                      ...prev,
                      limit: newLimit,
                      currentPage: 1,
                      nextCursor: null,
                      hasMore: false
                    }));
                    // Refresh with new limit
                    setTimeout(() => fetchSubmissions(), 100);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </BootstrapForm.Select>
              </div>

              <div className="text-muted small">
                <span>Total: {pagination.totalItems}</span>
                {pagination.totalItems > 0 && (
                  <span className="ms-2">• Page {pagination.currentPage}</span>
                )}
              </div>
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={refreshSubmissions}
                disabled={loading}
              >
                <FaEye className="me-1" />
                Refresh
              </Button>

              {pagination.hasMore && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={loadMoreSubmissions}
                  disabled={loadingMore || !pagination.nextCursor}
                >
                  {loadingMore ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load More
                      <FaChevronDown className="ms-1" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {groupedSubmissions.map((group, groupIndex) => (
            <div key={group.dateKey} className={groupIndex > 0 ? "mt-2" : ""}>
              {/* Collapsible Date Header */}
              <div
                className="d-flex align-items-center p-2 border rounded bg-light cursor-pointer"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleDateGroup(group.dateKey)}
              >
                {expandedDateGroups.has(group.dateKey) ? (
                  <FaChevronDown className="me-2 text-primary" />
                ) : (
                  <FaChevronRight className="me-2 text-primary" />
                )}
                <h6 className="mb-0 text-primary flex-grow-1">
                  {group.date}
                </h6>
                <Badge bg="secondary" className="ms-2">
                  {group.submissions.length}
                </Badge>
              </div>

              {/* Collapsible Submissions Table for this date */}
              {expandedDateGroups.has(group.dateKey) && (
                <div className="mt-2">
                  <Table responsive hover size="sm" className="border">
                    <thead className="table-light">
                      <tr>
                        <th>Time</th>
                        <th>Status</th>
                        <th>Patient MRN</th>
                        <th>Submitted By</th>
                        <th>Actions</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.submissions.map((submission) => (
                        <React.Fragment key={submission.submission_id}>
                          <tr className={
                            submission.submission_id === currentSubmissionId ? 'table-warning' :
                              submission.submission_status === 'invalidated' ? 'table-danger' : ''
                          }>
                            <td>
                              {submission.submission_status === 'draft'
                                ? (submission.draft_saved_at
                                  ? moment(submission.draft_saved_at).format('HH:mm')
                                  : moment(submission.submitted_at).format('HH:mm'))
                                : moment(submission.submitted_at).format('HH:mm')
                              }
                              {submission.submission_id === currentSubmissionId && (
                                <Badge bg="warning" className="ms-2">Current</Badge>
                              )}
                            </td>
                            <td>{getStatusBadge(submission)}</td>
                            <td>{getPatientMRN()}</td>
                            <td>{getUserName(submission.submitted_by, submission.submitted_by_name)}</td>
                            <td>{getActionButtons(submission)}</td>
                            <td>
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => toggleSubmissionDetails(submission.submission_id)}
                              >
                                <FaEye className="me-1" />
                                {expandedSubmissions.has(submission.submission_id) ? 'Hide' : 'View'} Details
                              </Button>
                            </td>
                          </tr>
                          {expandedSubmissions.has(submission.submission_id) && (
                            <tr>
                              <td colSpan="6">
                                <div className="p-3 bg-light">
                                  <div className="row mb-3">
                                    <div className="col-md-4">
                                      <div className="bg-white p-3 border rounded">
                                        <h6 className="text-primary mb-2">Basic Info</h6>
                                        <strong>Status:</strong> {getStatusBadge(submission)}<br />
                                        <strong>Form Version:</strong> {submission.form_version || submission.version || 1}<br />
                                        <strong>Submitted By:</strong> {getUserName(submission.submitted_by, submission.submitted_by_name)}<br />
                                        <strong>Date:</strong> {submission.submission_status === 'draft'
                                          ? (submission.draft_saved_at
                                            ? moment(submission.draft_saved_at).format('DD/MM/YYYY hh:mm A')
                                            : moment(submission.submitted_at).format('DD/MM/YYYY hh:mm A'))
                                          : moment(submission.submitted_at).format('DD/MM/YYYY hh:mm A')
                                        }
                                      </div>
                                    </div>
                                    <div className="col-md-8">
                                      <div className="bg-white p-3 border rounded">
                                        <h6 className="text-primary mb-2">Form Data</h6>
                                        {renderFormData(submission)}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Action History */}
                                  {renderActionHistory(submission.submission_id)}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          ))}


        </Card.Body>
      </Card>

      {/* Action Modal */}
      <Modal show={showActionModal} onHide={() => setShowActionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === 'addendum' && 'Add Addendum'}
            {actionType === 'acknowledge' && 'Acknowledge Submission'}
            {actionType === 'invalidate' && 'Invalidate Submission'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubmission && (
            <div className="mb-3">
              <Alert variant={selectedSubmission.submission_status === 'invalidated' ? 'danger' : 'info'}>
                <strong>Status:</strong> {getStatusBadge(selectedSubmission)}<br />
                <strong>Date:</strong> {selectedSubmission.submission_status === 'draft'
                  ? (selectedSubmission.draft_saved_at
                    ? moment(selectedSubmission.draft_saved_at).format('DD/MM/YYYY hh:mm A')
                    : moment(selectedSubmission.submitted_at).format('DD/MM/YYYY hh:mm A'))
                  : moment(selectedSubmission.submitted_at).format('DD/MM/YYYY hh:mm A')
                }
                {selectedSubmission.submission_status === 'invalidated' && (
                  <div className="mt-2">
                    <strong>⚠️ This submission has been invalidated and no further actions can be performed.</strong>
                  </div>
                )}
              </Alert>
            </div>
          )}

          <BootstrapForm.Group>
            <BootstrapForm.Label>
              <strong>Reason for {actionType}:</strong>
              <span className="text-danger ms-1">*</span>
            </BootstrapForm.Label>
            <BootstrapForm.Control
              as="textarea"
              rows={4}
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              placeholder={`Please provide a detailed reason for this ${actionType}...`}
              required
            />
          </BootstrapForm.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActionModal(false)} disabled={submittingAction}>
            Cancel
          </Button>
          <Button
            variant={actionType === 'invalidate' ? 'danger' : 'primary'}
            onClick={handleSubmitAction}
            disabled={submittingAction || !actionReason.trim() || (selectedSubmission && selectedSubmission.submission_status === 'invalidated')}
          >
            {submittingAction && <Spinner animation="border" size="sm" className="me-2" />}
            Submit {actionType}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FormSubmissionHistory;